import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SiteContent, ContentOverrides } from './types';
import { DEFAULT_CONTENT } from './defaults';
import { deepMerge, setByPath } from './merge';
import { makeContentStore, SaveMeta, HistoryEntry } from './store';

interface ContentCtxValue {
  content: SiteContent;          // defaults ⊕ committed ⊕ staged (what to render)
  ready: boolean;                // overrides finished loading
  storeKind: 'local' | 'remote';
  hasOverrides: boolean;
  // Staged-edit model: edits accumulate in `pending` and only reach the
  // datastore on save(). revert() drops them; content still reflects them live.
  setField: (path: string, value: unknown) => void;
  dirty: boolean;
  pendingCount: number;
  save: (meta?: SaveMeta) => Promise<void>;
  revert: () => void;
  resetOverrides: () => Promise<void>;
  history: (path: string) => Promise<HistoryEntry[]>;
  recentAudit: () => Promise<HistoryEntry[]>;
  uploadImage: (dataUrl: string, filename: string, meta?: SaveMeta) => Promise<string>;
}

const ContentCtx = createContext<ContentCtxValue | null>(null);

// Fold the committed overrides and the pending edits into a single override tree.
function applyPending(committed: ContentOverrides, pending: Record<string, unknown>): ContentOverrides {
  let out: object = committed;
  for (const [path, value] of Object.entries(pending)) {
    out = setByPath(out, path, value);
  }
  return out as ContentOverrides;
}

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const store = useMemo(() => makeContentStore(), []);
  const [committed, setCommitted] = useState<ContentOverrides>({});
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    store
      .load()
      .then(o => { if (live) { setCommitted(o); setReady(true); } })
      .catch(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [store]);

  const overrides = useMemo(() => applyPending(committed, pending), [committed, pending]);
  const content = useMemo(() => deepMerge(DEFAULT_CONTENT, overrides), [overrides]);

  // Stage a single edit — no I/O until save().
  const setField = useCallback((path: string, value: unknown) => {
    setPending(prev => ({ ...prev, [path]: value }));
  }, []);

  const revert = useCallback(() => setPending({}), []);

  const save = useCallback(async (meta?: SaveMeta) => {
    const entries = Object.entries(pending);
    if (!entries.length) return;
    const failed: Record<string, unknown> = {};
    for (const [path, value] of entries) {
      try {
        await store.saveField(path, value, meta);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('content save failed', path, err);
        failed[path] = value;
      }
    }
    // Fold the successes into committed; keep failures pending.
    const succeeded = entries.filter(([p]) => !(p in failed));
    if (succeeded.length) {
      setCommitted(prev => {
        let out: object = prev;
        for (const [p, v] of succeeded) out = setByPath(out, p, v);
        return out as ContentOverrides;
      });
    }
    setPending(failed);
    if (Object.keys(failed).length) {
      throw new Error(`${Object.keys(failed).length} field(s) failed to save`);
    }
  }, [pending, store]);

  const resetOverrides = useCallback(async () => {
    setPending({});
    setCommitted({});
    await store.clear().catch(() => {});
  }, [store]);

  const history = useCallback((path: string) => store.history(path), [store]);
  const recentAudit = useCallback(() => store.recentAudit(), [store]);
  const uploadImage = useCallback(
    (dataUrl: string, filename: string, meta?: SaveMeta) => store.uploadImage(dataUrl, filename, meta),
    [store]
  );

  const value: ContentCtxValue = {
    content,
    ready,
    storeKind: store.kind,
    hasOverrides: Object.keys(committed).length > 0 || Object.keys(pending).length > 0,
    setField,
    dirty: Object.keys(pending).length > 0,
    pendingCount: Object.keys(pending).length,
    save,
    revert,
    resetOverrides,
    history,
    recentAudit,
    uploadImage,
  };

  return <ContentCtx.Provider value={value}>{children}</ContentCtx.Provider>;
}

export function useContent(): ContentCtxValue {
  const c = useContext(ContentCtx);
  if (!c) throw new Error('useContent must be used within <ContentProvider>');
  return c;
}
